import {
  ComponentProps,
  FormEvent,
  Fragment,
  KeyboardEvent,
  useMemo,
  useState,
} from 'react';
import { Listbox, Transition } from '@headlessui/react';
import { ChevronDownIcon } from '@heroicons/react/24/outline';
import { JSONContent } from '@tiptap/core';
import { toast } from 'react-toastify';

import { Paragraph } from '../../../../common/Atoms/Typography/Paragraph';
import { classNames } from '../../../../common/lib/classNames';
import { TextEditor } from '../../Molecules/TextEditor/TextEditor';
import { useCurrentOrgUsers } from '../../../hooks/useCurrentOrgUsers';
import { useCreateConversationNoteMutation, useSendConversationMessageMutation } from '../../../services/api/aiAgentApi/aiAgent';

type TextEditorValue = ComponentProps<typeof TextEditor>[`value`];
type ComposerMode = `chat` | `note`;
type CurrentOrgUsers = ReturnType<typeof useCurrentOrgUsers>;

const COMPOSER_MODE_OPTIONS: Array<{ value: ComposerMode; label: string }> = [
  // Future modes (SMS/email) will be added here.
  { value: `chat`, label: `Chat with agent` },
  { value: `note`, label: `Internal note` },
];

function findMentions(json: JSONContent | null, orgUsers: CurrentOrgUsers) {
  if (!json) {
    return [];
  }

  const mentions: { label: string; value: string }[] = [];

  json.content?.forEach(content => {
    if (content.type === `mention`) {
      const mentionId = typeof content.attrs?.id === `string` ? content.attrs.id : ``;
      const mentionLabel = typeof content.attrs?.label === `string` ? content.attrs.label : ``;
      const mentionToken = mentionId || mentionLabel;
      const user = orgUsers.find(orgUser =>
        orgUser.value === mentionToken ||
        orgUser.label === mentionToken ||
        (mentionToken.length > 0 && orgUser.label.startsWith(`${mentionToken} (`)));

      if (user) {
        mentions.push(user);
      }
    }

    if (content.content) {
      mentions.push(...findMentions(content, orgUsers));
    }
  });

  return mentions;
}

interface ConversationComposerProps {
  organisationId: string;
  conversationId: string;
  isTerminalConversationStatus: boolean;
  isConversationArchived: boolean;
  isResolvingConversation: boolean;
  onResolveConversation: () => Promise<void> | void;
  currentOrgUsers: CurrentOrgUsers;
  chatDisabled?: boolean;
  chatDisabledReason?: string;
}

export function ConversationComposer({
  organisationId,
  conversationId,
  isTerminalConversationStatus,
  isConversationArchived,
  isResolvingConversation,
  onResolveConversation,
  currentOrgUsers,
  chatDisabled = false,
  chatDisabledReason,
}: ConversationComposerProps) {
  const [mode, setMode] = useState<ComposerMode>(`chat`);
  const [noteForm, setNoteForm] = useState<{
    content: string;
    error: string;
    jsonContent: JSONContent | null;
  }>({
    content: ``,
    error: ``,
    jsonContent: null,
  });
  const [chatMessage, setChatMessage] = useState({
    content: ``,
    error: ``,
  });
  const [createConversationNote, { isLoading: isCreatingConversationNote }] = useCreateConversationNoteMutation();
  const [sendConversationMessage, { isLoading: isSendingConversationMessage }] = useSendConversationMessageMutation();
  const selectedModeOption = useMemo(() => {
    return COMPOSER_MODE_OPTIONS.find(option => option.value === mode) || COMPOSER_MODE_OPTIONS[0];
  }, [mode]);
  const hasNoteContent = Boolean(noteForm.content.trim());
  const hasChatContent = Boolean(chatMessage.content.trim());
  const resolvedChatDisabledReason = chatDisabledReason
    || (isConversationArchived ? `Chat is unavailable on archived conversations.` : `Chat is unavailable on resolved or archived conversations.`);

  const handleNoteChange = ({ text, json }: { text: string; json: JSONContent }) => {
    if (text.length > 1500) {
      setNoteForm({
        content: text,
        error: `Note must be less than 1500 characters`,
        jsonContent: json,
      });

      return;
    }

    setNoteForm({
      content: text,
      error: ``,
      jsonContent: json,
    });
  };

  const createInternalNote = async() => {
    const content = noteForm.content.trim();

    if (!content || noteForm.error) {
      return false;
    }

    const mentionedUsers = findMentions(noteForm.jsonContent, currentOrgUsers)
      .map(mention => mention.value);

    try {
      await createConversationNote({
        organisationId,
        conversationId,
        content,
        mentionedUsers,
      }).unwrap();

      setNoteForm({
        content: ``,
        error: ``,
        jsonContent: null,
      });

      return true;
    }
    catch {
      toast.error(`Could not add internal note.`);

      return false;
    }
  };

  const handleCreateNote = async(event: FormEvent) => {
    event.preventDefault();
    await createInternalNote();
  };

  const handleResolveWithNote = async() => {
    const created = await createInternalNote();

    if (!created) {
      return;
    }

    await onResolveConversation();
  };

  const handleChatChange = (value: string) => {
    if (value.length > 1500) {
      setChatMessage({
        content: value,
        error: `Message must be less than 1500 characters`,
      });

      return;
    }

    setChatMessage({
      content: value,
      error: ``,
    });
  };

  const handleSendMessage = async() => {
    const content = chatMessage.content.trim();

    if (!content || chatMessage.error || chatDisabled) {
      return;
    }

    try {
      await sendConversationMessage({
        organisationId,
        conversationId,
        content,
      }).unwrap();

      setChatMessage({
        content: ``,
        error: ``,
      });
    }
    catch {
      toast.error(`Could not send message.`);
    }
  };

  const handleChatKeyDown = (event: KeyboardEvent<HTMLTextAreaElement>) => {
    if (event.key !== `Enter` || event.shiftKey) {
      return;
    }

    event.preventDefault();
    void handleSendMessage();
  };

  return (
    <div className={ classNames(
      `mt-3 rounded-lg border`,
      mode === `note` ? `border-amber-200 bg-amber-50/60` : `border-gray-200 bg-gray-50/60`,
    ) }>
      <div className={ `px-2 pt-2` }>
        <Listbox value={ mode }
          onChange={ setMode }>
          { ({ open }) => (
            <div className={ `relative inline-block` }>
              <Listbox.Button className={ `inline-flex cursor-pointer items-center gap-1 rounded-md px-1.5 py-1 text-sm font-semibold text-gray-700 transition-colors duration-200 hover:bg-black/5 focus:outline-hidden` }>
                <span>{ selectedModeOption.label }</span>
                <ChevronDownIcon className={ `h-4 w-4 text-gray-500 transition-transform duration-200 ${open ? `rotate-180` : ``}` } />
              </Listbox.Button>
              <Transition
                as={ Fragment }
                leave={ `transition ease-in duration-100` }
                leaveFrom={ `opacity-100` }
                leaveTo={ `opacity-0` }>
                <Listbox.Options className={ `absolute z-20 mt-2 min-w-[160px] rounded-xl border border-gray-200 bg-white p-1 shadow-lg` }>
                  { COMPOSER_MODE_OPTIONS.map(option => (
                    <Listbox.Option
                      key={ option.value }
                      value={ option.value }>
                      { ({ active, selected }) => (
                        <div className={ `cursor-pointer rounded-lg px-3 py-2 text-sm font-medium ${active ? `bg-paidniceGreen-50 text-paidniceGreen-700` : `text-gray-700`} ${selected ? `text-paidniceGreen-700` : ``}` }>
                          { option.label }
                        </div>
                      ) }
                    </Listbox.Option>
                  )) }
                </Listbox.Options>
              </Transition>
            </div>
          ) }
        </Listbox>
      </div>

      {
        mode === `note` &&
        <form onSubmit={ event => {
          void handleCreateNote(event);
        } }
        >
          <TextEditor
            mode={ `note` }
            value={ (noteForm.jsonContent || ``) as TextEditorValue }
            onChange={ handleNoteChange }
            mentionValues={ currentOrgUsers.map(user => user.label) }
            placeholder={ `Write an internal note (not sent to the customer)... use @ to mention someone` }
            editorClassname={ classNames(
              `min-h-[64px] border-transparent bg-transparent px-3 py-2 text-sm hover:border-transparent focus:border-transparent`,
              noteForm.error ? `border-red-500` : ``,
            ) }
          />
          {
            noteForm.error &&
            <Paragraph
              variant={ `error` }
              size={ `xs` }
              className={ `px-3` }
            >
              { noteForm.error }
            </Paragraph>
          }
          <div className={ `flex justify-end gap-2 px-2 pb-2 pt-1` }>
            <button
              type={ `submit` }
              className={ `cursor-pointer rounded-md border border-gray-200 px-2.5 py-1 text-sm text-gray-700 transition-colors duration-200 hover:border-paidniceGreen-500 hover:bg-paidniceGreen-500 hover:text-white focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-paidniceGreen-400 disabled:cursor-not-allowed disabled:bg-gray-200 disabled:text-gray-500` }
              disabled={ isCreatingConversationNote || !hasNoteContent || Boolean(noteForm.error) }
            >
              { isCreatingConversationNote ? `Adding note...` : `Add internal note` }
            </button>
            <button
              type={ `button` }
              className={ `cursor-pointer rounded-md border border-paidniceGreen-600 bg-paidniceGreen-600 px-2.5 py-1 text-sm text-white transition-colors duration-200 hover:border-paidniceGreen-700 hover:bg-paidniceGreen-700 focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-paidniceGreen-400 disabled:cursor-not-allowed disabled:border-gray-200 disabled:bg-gray-200 disabled:text-gray-500` }
              disabled={
                isCreatingConversationNote
                || isResolvingConversation
                || isTerminalConversationStatus
                || (hasNoteContent && Boolean(noteForm.error))
              }
              onClick={ () => {
                if (hasNoteContent) {
                  void handleResolveWithNote();

                  return;
                }

                void onResolveConversation();
              } }
            >
              { isResolvingConversation ? `Resolving...` : (hasNoteContent ? `Resolve with note` : `Resolve Conversation`) }
            </button>
          </div>
        </form>
      }

      {
        mode === `chat` &&
        <div>
          <textarea
            rows={ 3 }
            value={ chatMessage.content }
            maxLength={ 1500 }
            placeholder={ `Reply to the agent (not sent to the customer)... e.g. "yes" to approve, or tell it what to do differently` }
            className={ classNames(
              `block w-full resize-none border-0 bg-transparent px-3 py-2 text-sm text-gray-900 placeholder:text-gray-400 focus:ring-0 focus:outline-hidden`,
              chatDisabled && `text-gray-400`,
            ) }
            disabled={ chatDisabled || isSendingConversationMessage }
            onChange={ event => handleChatChange(event.target.value) }
            onKeyDown={ handleChatKeyDown }
          />
          {
            chatMessage.error &&
            <Paragraph
              variant={ `error` }
              size={ `xs` }
              className={ `px-3` }
            >
              { chatMessage.error }
            </Paragraph>
          }
          {
            chatDisabled &&
            <Paragraph
              size={ `xs` }
              variant={ `secondary` }
              className={ `px-3` }
            >
              { resolvedChatDisabledReason }
            </Paragraph>
          }
          <div className={ `flex justify-end px-2 pb-2 pt-1` }>
            <button
              type={ `button` }
              className={ `cursor-pointer rounded-md border border-paidniceGreen-600 bg-paidniceGreen-600 px-2.5 py-1 text-sm text-white transition-colors duration-200 hover:border-paidniceGreen-700 hover:bg-paidniceGreen-700 focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-paidniceGreen-400 disabled:cursor-not-allowed disabled:border-gray-200 disabled:bg-gray-200 disabled:text-gray-500` }
              disabled={ isSendingConversationMessage || !hasChatContent || Boolean(chatMessage.error) || chatDisabled }
              onClick={ () => {
                void handleSendMessage();
              } }
            >
              { isSendingConversationMessage ? `Sending...` : `Send` }
            </button>
          </div>
        </div>
      }
    </div>
  );
}
